import type { DraftEntityType, UserDraft } from '@gng-ehs/shared';
import { api } from './api.js';

const STORAGE_KEY = 'gng_user_drafts_v1';
const DEBOUNCE_DELAY_MS = 2500;

interface DraftStoreData {
  [draftId: string]: UserDraft;
}

// In-memory store & pending server debounces
let storeCache: DraftStoreData | null = null;
const debounceTimers = new Map<string, ReturnType<typeof setTimeout>>();

function getStore(): DraftStoreData {
  if (storeCache !== null) return storeCache;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    storeCache = raw ? JSON.parse(raw) : {};
  } catch (err) {
    console.warn('Failed to read drafts from localStorage:', err);
    storeCache = {};
  }
  return storeCache!;
}

function persistStore(data: DraftStoreData): void {
  storeCache = data;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (err) {
    console.warn('Failed to write drafts to localStorage:', err);
  }
  notifyChange();
}

function notifyChange(): void {
  if (typeof window === 'undefined') return;
  const count = Object.keys(getStore()).length;
  window.dispatchEvent(new CustomEvent('gng-drafts-changed', { detail: { count } }));
}

export const draftManager = {
  /**
   * Save a draft. Updates local storage immediately, then debounces sync to worker.
   */
  saveDraft(
    id: string,
    entityType: DraftEntityType,
    title: string,
    subtitle: string | undefined,
    formData: Record<string, unknown>
  ): UserDraft {
    const now = new Date().toISOString();
    const existing = this.getDraft(id);

    const draft: UserDraft = {
      id,
      entity_type: entityType,
      title,
      subtitle,
      form_data: formData,
      created_at: existing?.created_at || now,
      updated_at: now,
    };

    const store = { ...getStore(), [id]: draft };
    persistStore(store);

    // Cancel existing debounce timer for this draft if running
    if (debounceTimers.has(id)) {
      clearTimeout(debounceTimers.get(id)!);
    }

    // Schedule debounced sync to server
    const timer = setTimeout(() => {
      debounceTimers.delete(id);
      api.drafts
        .save(id, {
          entity_type: entityType,
          title,
          subtitle,
          form_data: formData,
        })
        .catch((err) => {
          console.warn(`[DraftManager] Background sync failed for draft ${id}:`, err);
        });
    }, DEBOUNCE_DELAY_MS);

    debounceTimers.set(id, timer);
    return draft;
  },

  /**
   * Flush any pending debounce to the server immediately.
   */
  flushDraft(id: string): void {
    if (debounceTimers.has(id)) {
      clearTimeout(debounceTimers.get(id)!);
      debounceTimers.delete(id);
      const draft = this.getDraft(id);
      if (draft) {
        api.drafts
          .save(id, {
            entity_type: draft.entity_type,
            title: draft.title,
            subtitle: draft.subtitle,
            form_data: draft.form_data,
          })
          .catch(() => {});
      }
    }
  },

  /**
   * Retrieve a draft by ID from local cache.
   */
  getDraft(id: string): UserDraft | null {
    const store = getStore();
    return store[id] || null;
  },

  /**
   * Get the most recently updated draft for a specific entity type.
   */
  getLatestDraftFor(entityType: DraftEntityType): UserDraft | null {
    const list = this.listDrafts(entityType);
    return list.length > 0 ? list[0] : null;
  },

  /**
   * List all drafts, optionally filtered by entity type, sorted by latest updated.
   */
  listDrafts(entityType?: DraftEntityType): UserDraft[] {
    const store = getStore();
    let drafts = Object.values(store);
    if (entityType && entityType !== ('all' as unknown)) {
      drafts = drafts.filter((d) => d.entity_type === entityType);
    }
    return drafts.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
  },

  /**
   * Delete a draft locally and on the server.
   */
  deleteDraft(id: string): void {
    if (debounceTimers.has(id)) {
      clearTimeout(debounceTimers.get(id)!);
      debounceTimers.delete(id);
    }

    const store = { ...getStore() };
    delete store[id];
    persistStore(store);

    api.drafts.delete(id).catch(() => {});
  },

  /**
   * Clear all drafts (or filtered by entity type).
   */
  clearAllDrafts(entityType?: DraftEntityType): void {
    // Clear any timers
    debounceTimers.forEach((timer) => clearTimeout(timer));
    debounceTimers.clear();

    if (!entityType || entityType === ('all' as unknown)) {
      persistStore({});
    } else {
      const store = { ...getStore() };
      for (const [key, val] of Object.entries(store)) {
        if (val.entity_type === entityType) {
          delete store[key];
        }
      }
      persistStore(store);
    }

    api.drafts.clearAll(entityType).catch(() => {});
  },

  /**
   * Sync drafts from the server and merge with local store.
   */
  async syncFromServer(): Promise<UserDraft[]> {
    try {
      const res = await api.drafts.list();
      const serverDrafts = res.data || [];
      const store = { ...getStore() };

      for (const sd of serverDrafts) {
        const local = store[sd.id];
        // If local doesn't exist or server is newer, adopt server draft
        if (!local || new Date(sd.updated_at).getTime() > new Date(local.updated_at).getTime()) {
          store[sd.id] = sd;
        }
      }

      persistStore(store);
      return this.listDrafts();
    } catch (err) {
      console.warn('[DraftManager] Failed to sync drafts from server:', err);
      return this.listDrafts();
    }
  },

  /**
   * Subscribe to count changes for badges.
   */
  subscribe(listener: (count: number) => void): () => void {
    const handler = (e: Event) => {
      const customEvent = e as CustomEvent<{ count: number }>;
      listener(customEvent.detail?.count ?? Object.keys(getStore()).length);
    };
    window.addEventListener('gng-drafts-changed', handler);
    // Initial call
    listener(Object.keys(getStore()).length);
    return () => {
      window.removeEventListener('gng-drafts-changed', handler);
    };
  },
};
